"use client";

import React, { useState } from "react";
import { Plus, Minus, FileCheck } from 'lucide-react';
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
interface FaqItem {
  id: string;
  indexNum: string;
  question: string;
  answer: string;
}
const FAQ_ITEMS: FaqItem[] = [{
  id: "q1",
  indexNum: "01",
  question: "Is buying a used mechanical bull worth it?",
  answer: "A used mechanical bull can offer good value when the price reflects the machine's condition, included equipment and available history. Evaluate the exact unit rather than assuming every used bull represents the same level of value."
}, {
  id: "q2",
  indexNum: "02",
  question: "How much does a used mechanical bull cost?",
  answer: "Used mechanical bull prices vary by model, age, condition, controller, inflatable, included accessories and current availability. Current inventory provides a more useful comparison than one generic used-bull price."
}, {
  id: "q3",
  indexNum: "03",
  question: "What should I check before buying a used mechanical bull?",
  answer: "Review the controller, motion base, inflatable, blower, included equipment, known repairs, available history, power requirements, parts availability and shipping information."
}, {
  id: "q4",
  indexNum: "04",
  question: "Do used mechanical bulls come with an inflatable arena?",
  answer: "Some do and some do not. Check the individual listing to confirm whether the inflatable safety arena and blower are included with the exact machine."
}, {
  id: "q5",
  indexNum: "05",
  question: "Should I ask for an operating video?",
  answer: "An operating video can provide additional information about the exact machine when one is available. It should supplement rather than replace the other condition information in the listing."
}, {
  id: "q6",
  indexNum: "06",
  question: "Are used mechanical bulls inspected before sale?",
  answer: "Inspection practices vary by seller and machine. Mechanical Bull Shop should only state that a particular unit was inspected or tested when genuine documentation or first-hand verification supports that claim."
}, {
  id: "q7",
  indexNum: "07",
  question: "Can I replace parts on an older mechanical bull?",
  answer: "It depends on the model and component. Check parts availability and compatibility before buying older equipment, especially for controllers and model-specific electrical components."
}, {
  id: "q8",
  indexNum: "08",
  question: "Can used mechanical bulls be shipped?",
  answer: "Yes, depending on the machine and destination. Delivery requirements vary according to the equipment, packed size, weight, origin, destination and unloading access."
}];
export default function UsedFaq() {
  const [openItems, setOpenItems] = useState<string[]>(["q1"]);
  const shouldReduceMotion = useReducedMotion();
  const toggleItem = (id: string) => {
    setOpenItems(prev => prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]);
  };
  return <section id="usedmechanicalbullfaq" data-nav="light" className="bg-background text-foreground py-20 lg:py-28 border-b border-border">
      <div className="container mx-auto px-4 max-w-5xl">
        {/* Header Block */}
        <motion.div initial={shouldReduceMotion ? false : {
        opacity: 0,
        y: 12
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.3
      }} className="mb-12 lg:mb-16">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div>
              <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight uppercase leading-none text-foreground">
                Used Mechanical Bull FAQ
              </h2>
              <p className="mt-3 text-muted-foreground text-sm sm:text-base font-sans max-w-xl">
                Straight answers to common questions about used mechanical bull condition, included equipment, pricing, parts and shipping.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Industrial Accordion Grid */}
        <div className="border-t border-border">
          {FAQ_ITEMS.map((item, index) => {
          const isOpen = openItems.includes(item.id);
          return <div key={item.id} data-index={index} className="border-b border-border bg-card transition-colors duration-150">
                <Button variant="ghost" type="button" onClick={() => toggleItem(item.id)} aria-expanded={isOpen} aria-controls={`faq-answer-${item.id}`} className="w-full text-left py-5 sm:py-6 px-4 sm:px-6 flex items-start sm:items-center justify-between gap-4 cursor-pointer hover:bg-muted/40 transition-colors group h-auto rounded-none font-normal">
                  <div className="flex items-start sm:items-center gap-4 sm:gap-6 flex-1 pr-2">
                    <h3 className="font-serif text-xl sm:text-2xl font-bold tracking-normal uppercase text-foreground leading-snug group-hover:text-primary transition-colors">
                      {item.question}
                    </h3>
                  </div>

                  <div className="shrink-0 pt-0.5 sm:pt-0">
                    <span className="inline-flex items-center justify-center w-8 h-8 border border-border bg-background group-hover:border-primary transition-colors">
                      {isOpen ? <Minus className="w-4 h-4 text-primary" /> : <Plus className="w-4 h-4 text-foreground group-hover:text-primary transition-colors" />}
                    </span>
                  </div>
                </Button>

                <AnimatePresence initial={false}>
                  {isOpen && <motion.div id={`faq-answer-${item.id}`} initial={shouldReduceMotion ? {
                opacity: 1,
                height: "auto"
              } : {
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: "auto"
              }} exit={shouldReduceMotion ? {
                opacity: 0,
                height: "auto"
              } : {
                opacity: 0,
                height: 0
              }} transition={{
                duration: 0.22,
                ease: [0.2, 0, 0, 1]
              }} className="overflow-hidden">
                      <div className="px-4 sm:px-6 pb-6 pt-1 sm:pl-16 text-muted-foreground text-sm sm:text-base leading-relaxed font-sans border-t border-border/40">
                        <p>{item.answer}</p>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>;
        })}
        </div>

        {/* Industrial Support Footer Note */}
        <motion.div initial={shouldReduceMotion ? false : {
        opacity: 0,
        y: 10
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.3,
        delay: 0.1
      }} className="mt-12 p-6 border border-border bg-muted/30 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 border border-border bg-card flex items-center justify-center shrink-0">
              <FileCheck className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h4 className="font-serif text-lg font-bold uppercase tracking-wide text-foreground">
                Need more information about a specific bull?
              </h4>
              <p className="text-xs sm:text-sm text-muted-foreground font-sans mt-0.5">
                Send us the product name or SKU and the available information for that particular listing can be reviewed in context.
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
            <Button asChild variant="outline" className="font-mono text-xs tracking-wider uppercase flex-1 md:flex-none">
              <Link to="/UsedMechanicalBullsPage">
                VIEW INVENTORY
              </Link>
            </Button>
            <Button asChild variant="default" className="font-mono text-xs tracking-wider uppercase flex-1 md:flex-none">
              <Link to="/RequestQuotePage">
                REQUEST PRODUCT INFORMATION
              </Link>
            </Button>
          </div>
        </motion.div>
      </div>
    </section>;
}